import { AgGridAngular } from "@ag-grid-community/angular";
import {
  ColDef,
  GetGroupRowAggParams,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  RowDataUpdatedEvent,
} from "@ag-grid-community/core";
import { Component, computed, DestroyRef, inject, linkedSignal, signal, ChangeDetectionStrategy } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { AccountState, AccountType, Currency } from "@front/m19-api-client";
import { getFilteredLeafNodes, gridConfig } from "@front/m19-grid-config";
import { COST, Metric } from "@front/m19-metrics";
import { AdStatsEx } from "@front/m19-models";
import {
  AdStatsType,
  AuthService,
  MetricsSelectorPage,
  MetricsSelectorKey,
  StatsTypeService,
  UserSelectionService,
} from "@front/m19-services";
import {
  ActionButton,
  ActionButtonsComponent,
  ChartConfig,
  IButtonDirective,
  IChartComponent,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuTriggerDirective,
  IIconComponent,
  ISelectMenuImports,
  ModalOptions,
  ModalService,
} from "@front/m19-ui";
import { DateAggregation, mergeSeveralDates } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ChartLayoutComponent } from "@m19-board/containers/chart-layout/chart-layout.component";
import { actionColumnProperties, currencyColumn, MarketplaceColumn } from "@m19-board/grid-config/grid-columns";
import { restoreDefaultColumns, STATUS_BAR } from "@m19-board/grid-config/grid-config";
import { GridConfigService } from "@m19-board/grid-config/grid-config.service";
import { MetricSelectorComponent } from "@m19-board/insights/metric-selector/metric-selector.component";
import { ChartModalComponent, ChartModalInputData } from "@m19-board/shared/chart-modal/chart-modal.component";
import { LinkComponent } from "@m19-board/shared/link/link.component";
import { map, Observable, startWith } from "rxjs";
import { HomeGridStats, HomeService } from "./home.service";
import { ActivityEventAnnotationsService } from "../activities/activity-event-annotations.service";
import { DataSetEventAnnotation, StatDisplayMode } from "@front/m19-models/dataset";
import { DefaultAdStatsMetricsWithSameScale } from "@front/m19-models/dataset/Dataset";
import {
  AdType,
  CompactFilterComponent,
  Filter,
  FilterCategory,
} from "@m19-board/shared/compact-filter/compact-filter.component";
import { CompactFilterService } from "@m19-board/shared/compact-filter/compact-filter.service";

@Component({
  selector: "app-home",
  templateUrl: "./home.component.html",
  imports: [
    TranslocoDirective,
    MetricSelectorComponent,
    ChartLayoutComponent,
    ISelectMenuImports,
    AgGridAngular,
    IDropdownMenuTriggerDirective,
    IDropdownMenuComponent,
    IDropdownMenuItemDirective,
    IButtonDirective,
    IIconComponent,
    IChartComponent,
    CompactFilterComponent,
  ],
  providers: [HomeService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent {
  private readonly homeService = inject(HomeService);
  private readonly statsTypeService = inject(StatsTypeService);
  private readonly translocoService = inject(TranslocoService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly gridConfigService = inject(GridConfigService);
  private readonly authService = inject(AuthService);
  private readonly modalService = inject(ModalService);
  private readonly activityEventAnnotationsService = inject(ActivityEventAnnotationsService);
  private readonly compactFilterService = inject(CompactFilterService);
  readonly destroyRef = inject(DestroyRef);

  readonly StatDisplayMode = StatDisplayMode;
  readonly localStorageKey: MetricsSelectorKey = { page: MetricsSelectorPage.superBoard };
  readonly CHARTS_METRICS = this.homeService.CHARTS_METRICS;
  readonly GRID_METRICS = this.homeService.GRID_METRICS;
  readonly STATUS_BAR = STATUS_BAR;

  readonly loading = toSignal(this.homeService.loading$, { initialValue: true });

  private _stats = toSignal(this.homeService.selectedAccountMarketplacesStats$, { initialValue: [] });
  private _previousStats = toSignal(this.homeService.selectedAccountMarketplacesPreviousStats$, { initialValue: [] });

  private dateRange = toSignal(this.userSelectionService.dateRange$, { requireSync: true });
  private selectedDateRange = toSignal(this.userSelectionService.selectedDateRange$, { requireSync: true });
  private previousDateRange = toSignal(this.userSelectionService.periodComparison$, { requireSync: true });
  private currency = toSignal(this.userSelectionService.selectedCurrency$, { requireSync: true });
  private locale = toSignal(this.authService.loggedUser$.pipe(map((user) => user.locale)), { requireSync: true });
  protected hasDspAdvertisers = toSignal(
    this.homeService.allAccountMarketplaces$.pipe(
      map((accountMarketplaces) => accountMarketplaces.some((am) => am.dspAdvertiserId)),
      startWith(false),
    ),
    { requireSync: true },
  );

  protected stats = computed(() => {
    return this.statsTypeService.mapAndConvertDataToStatsType(this._stats(), this.statsDisplayMode());
  });
  private previousStats = computed(() => {
    return this.statsTypeService.mapAndConvertDataToStatsType(this._previousStats(), this.statsDisplayMode());
  });
  totalStats = computed(() => {
    return this.mergeStats(this.stats());
  });
  previousTotalStats = computed(() => {
    return this.mergeStats(this.previousStats());
  });

  private totalManagedStats = computed(() => {
    return this.mergeStats(this.getStatsTypeStats(this.stats(), AdStatsType.MANAGED));
  });
  private previousTotalManagedStats = computed(() => {
    return this.mergeStats(this.getStatsTypeStats(this.previousStats(), AdStatsType.MANAGED));
  });
  private totalUnmanagedStats = computed(() => {
    return this.mergeStats(this.getStatsTypeStats(this.stats(), AdStatsType.UNMANAGED));
  });
  private previousTotalUnmanagedStats = computed(() => {
    return this.mergeStats(this.getStatsTypeStats(this.previousStats(), AdStatsType.UNMANAGED));
  });

  splitStats = computed<{ splitData1: AdStatsEx; splitData2: AdStatsEx; type: AdStatsType } | undefined>(() => {
    if (this.statsDisplayMode() !== StatDisplayMode.SPLIT) return;
    return { splitData1: this.totalManagedStats(), splitData2: this.totalUnmanagedStats(), type: AdStatsType.MANAGED };
  });
  previousSplitStats = computed<{ splitData1: AdStatsEx; splitData2: AdStatsEx; type: AdStatsType } | undefined>(() => {
    if (this.statsDisplayMode() !== StatDisplayMode.SPLIT) return;
    return {
      splitData1: this.previousTotalManagedStats(),
      splitData2: this.previousTotalUnmanagedStats(),
      type: AdStatsType.MANAGED,
    };
  });

  displaySplitStatsMenu = computed(() => {
    return this.canSplitStats(this._stats());
  });

  gridData = signal<HomeGridStats[]>([]);
  displayGlobalChart = signal<boolean>(true);
  gridApi: GridApi<HomeGridStats> | undefined;

  private readonly currencyColumn: ColDef<HomeGridStats, Currency> = currencyColumn<HomeGridStats>(() =>
    this.currency(),
  );

  adTypeFilters = computed<Filter<FilterCategory.AdType>[]>(() => [
    this.compactFilterService.getFilterWithOptions<FilterCategory.AdType>(
      FilterCategory.AdType,
      [AdType.SponsoredAds, AdType.DSP],
      {
        localStorageKey: "adType_filters_home",
      },
    ),
  ]);

  selectedMetrics = signal<Metric<AdStatsEx>[]>([]);
  dateAggregation = signal<DateAggregation>(DateAggregation.daily);
  statsDisplayMode = linkedSignal<boolean, StatDisplayMode>({
    source: this.displaySplitStatsMenu,
    computation: (displaySplitStatsMenu, currentDisplayMode) => {
      if (!displaySplitStatsMenu) return StatDisplayMode.ALL;
      return currentDisplayMode?.value ?? StatDisplayMode.ALL;
    },
  });

  chartConfig = computed<ChartConfig<AdStatsEx, "line">>(() => ({
    type: "line",
    aggFunctions: [mergeSeveralDates],
    aspectRatio: 3,
    metrics: this.selectedMetrics(),
    metricsOnSameScale: DefaultAdStatsMetricsWithSameScale,
    dateAggregation: this.dateAggregation(),
    dateRange: this.selectedDateRange(),
    previousPeriodData: this.previousStats(),
    previousPeriod: this.previousDateRange()?.period,
  }));

  readonly gridOptions: GridOptions<HomeGridStats> = {
    ...gridConfig({
      gridKey: "homeGrid",
      withFooterRow: true,
      autoSizeDefault: true,
    }),
    defaultColDef: {
      sortable: true,
      filter: true,
      resizable: true,
      cellClass: "sensitive-data",
    },
    showOpenedGroup: false,
    rowGroupPanelShow: "always",
    autoGroupColumnDef: {
      pinned: "left",
    },
    enableCharts: true,
    onGridReady: (params: GridReadyEvent) => {
      this.onGridReady(params);
    },
    onRowDataUpdated: (event: RowDataUpdatedEvent<HomeGridStats>) => {
      event.api.redrawRows();
    },
    getRowId: (params: GetRowIdParams<HomeGridStats>) => params.data.rowId,
    getGroupRowAgg: (params: GetGroupRowAggParams<HomeGridStats>) => {
      const result: AdStatsEx = {};
      params.nodes.forEach((node: IRowNode<HomeGridStats>) => {
        const rowsToProcess: IRowNode<HomeGridStats>[] = getFilteredLeafNodes(node, params?.api);
        const data = rowsToProcess
          .map((row) => row.data!)
          .reduce((acc, curr) => mergeSeveralDates(acc, curr!), {} as AdStatsEx);
        mergeSeveralDates(result, data);
      });
      return result;
    },
    columnDefs: [
      {
        field: "organizationName",
        headerName: this.translocoService.translate("account-setting.organization"),
        pinned: "left",
        hide: true,
        filter: "agTextColumnFilter",
        floatingFilter: true,
        enableRowGroup: true,
      },
      {
        field: "accountGroupName",
        headerName: this.translocoService.translate("agency-board.account_group"),
        pinned: "left",
        hide: true,
        filter: "agTextColumnFilter",
        floatingFilter: true,
        enableRowGroup: true,
      },
      {
        field: "accountName",
        headerValueGetter: () => this.translocoService.translate("common.account"),
        headerName: this.translocoService.translate("common.account", {}, "en"),
        pinned: "left",
        cellRendererSelector: (params: ICellRendererParams<HomeGridStats>) => {
          if (!params.data) return;
          return {
            component: LinkComponent,
            params: {
              routerLink: "/dashboard/advertising",
              queryParams: {
                accountId: params.data?.accountId,
                marketplace: params.data?.marketplace,
                orgId: params.data?.organizationId,
              },
              content: params.value,
              queryParamsHandling: "merge",
            },
          };
        },
        enableRowGroup: true,
        cellClass: "sensitive-data",
        filter: "agTextColumnFilter",
        floatingFilter: true,
      },
      {
        ...MarketplaceColumn(this.translocoService),
        pinned: "left",
        enableRowGroup: true,
      },
      {
        colId: "state",
        headerValueGetter: () => this.translocoService.translate("billing-customer.state"),
        headerName: this.translocoService.translate("billing-customer.state", {}, "en"),
        pinned: "left",
        hide: true,
        enableRowGroup: true,
        valueGetter: (params) => {
          switch (params.data?.state) {
            case AccountState.BIDDER_ON:
              return this.translocoService.translate("agency-board.ads_automation");
            case AccountState.DOWNLOADER_ON:
              return this.translocoService.translate("agency-board.pull_stats");
            default:
              return "-";
          }
        },
        filter: "agSetColumnFilter",
        floatingFilter: true,
      },
      {
        headerValueGetter: () => this.translocoService.translate("agency-board.account_type"),
        headerName: this.translocoService.translate("agency-board.account_type", {}, "en"),
        pinned: "left",
        hide: true,
        enableRowGroup: true,
        valueGetter: (params) => {
          switch (params.data?.accountType) {
            case AccountType.SELLER:
              return this.translocoService.translate("common.seller");
            case AccountType.VENDOR:
              return this.translocoService.translate("common.vendor");
            default:
              return "-";
          }
        },
        floatingFilter: true,
        filter: "agSetColumnFilter",
      },
      this.currencyColumn,
      ...this.gridConfigService
        .getMetricsColDef<HomeGridStats>(this.GRID_METRICS)
        .map((def: ColDef<HomeGridStats>) => ({
          ...def,
          cellRendererParams: (params: ICellRendererParams<HomeGridStats>) => {
            const previousData = this.getPreviousNodeData(params.node);
            return {
              ...def.cellRendererParams(params),
              previousData: previousData,
              locale: this.locale(),
              currency: this.currency(),
            };
          },
        })),
      {
        ...actionColumnProperties<HomeGridStats, string>(),
        cellRendererSelector: (params) => {
          return {
            component: ActionButtonsComponent,
            params: {
              actionButtons: [
                {
                  icon: "chartLine",
                  tooltip: this.translocoService.translate("agency-board.display_graph"),
                  onClick: (params: ICellRendererParams<HomeGridStats>) => {
                    this.openRowChart(params);
                  },
                },
              ] as ActionButton[],
            },
          };
        },
      },
    ],
  };

  applyFilter(filter: Filter<FilterCategory.AdType>[]) {
    const values = filter[0].values;
    this.homeService.selectAdTypes(values);
  }

  selectMetrics(metrics: Metric<AdStatsEx>[]) {
    this.selectedMetrics.set(metrics);
  }

  restoreColumns() {
    restoreDefaultColumns("agencyBoardGrid", this.gridApi!);
  }

  exportGlobalDataCsv() {
    this.homeService.exportCsv(this.gridApi!, this.statsDisplayMode(), true, this.dateRange());
  }

  exportDailyDataCsv() {
    this.homeService.exportCsv(this.gridApi!, this.statsDisplayMode(), false, this.dateRange());
  }

  private getPreviousNodeData(node: IRowNode<HomeGridStats>): AdStatsEx | undefined {
    const rows = getFilteredLeafNodes(node)
      .map((leaf) => leaf.data)
      .filter((data) => !!data);
    return this.homeService.mergePreviousStats(rows);
  }

  private openRowChart(params: ICellRendererParams<HomeGridStats>) {
    const rowsToProcess: IRowNode[] = getFilteredLeafNodes(params.node, params.api);
    const chartData$ = this.homeService.getRowChartData(rowsToProcess);

    const title = params.node.isRowPinned()
      ? "Total"
      : params.data
        ? `${params.data.accountName} (${params.data.marketplace})`
        : params.node.key;

    let annotations$: Observable<DataSetEventAnnotation[]> | undefined = undefined;
    if (rowsToProcess.length === 1) {
      annotations$ = this.activityEventAnnotationsService.getStrategiesActivityEventAnnotation();
    }

    const modalOption: ModalOptions<ChartModalInputData<AdStatsEx>> = {
      modalTitle: title ?? undefined,
      size: "xl",
      data: {
        locale: this.locale()!,
        currency: this.currency()!,
        dataSetConfiguration: {
          aggregationFunction: [mergeSeveralDates],
          metricsOnSameScale: DefaultAdStatsMetricsWithSameScale,
        },
        annotations$,
        chartData$: chartData$,
        selectedMetrics: this.selectedMetrics(),
        metrics: this.GRID_METRICS,
        localStorageKey: this.localStorageKey,
      },
    };
    this.modalService.openModal<ChartModalInputData<AdStatsEx>, void>(ChartModalComponent, modalOption);
  }

  private onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.homeService.gridData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.gridData.set(data);
    });
  }

  private mergeStats(stats: AdStatsEx[]) {
    return this.homeService.mergeStats(stats);
  }

  private getStatsTypeStats(stats: AdStatsEx[], statsType: AdStatsType) {
    return stats.filter((stat) => stat.statsType === statsType);
  }

  private canSplitStats(stats: AdStatsEx[]) {
    const convertedStats = this.statsTypeService.convertDataToStatsType(stats, StatDisplayMode.SPLIT);
    const managedStats = this.mergeStats(this.getStatsTypeStats(convertedStats, AdStatsType.MANAGED));
    const unmanagedStats = this.mergeStats(this.getStatsTypeStats(convertedStats, AdStatsType.UNMANAGED));
    const managedCost = COST.value(managedStats);
    const unmanagedCost = COST.value(unmanagedStats);
    return managedCost !== undefined && unmanagedCost !== undefined && managedCost > 0 && unmanagedCost > 0;
  }
}
